import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { fmtDate } from "@/lib/api";
import {
  useApi, post, put, Panel, PageHead, Stat, QuickForm, DataTable, StatusBadge, Tabs,
} from "@/crm/ui";

const TABS = { Today: "today", Overdue: "overdue", Upcoming: "upcoming", All: "" };

export default function FollowUps() {
  const nav = useNavigate();
  const [tab, setTab] = useState("Today");
  const [form, setForm] = useState({});
  const [row, setRow] = useState(null);
  const [close, setClose] = useState({ status: "Completed", outcome: "", next_followup: "" });
  const due = TABS[tab];
  const list = useApi(`/crm/followups${due ? `?due=${due}` : ""}`, [tab]);
  const all = useApi("/crm/followups");
  const leads = useApi("/crm/leads");
  const options = useApi("/crm/options");
  const rows = list.data || [];
  const every = all.data || [];

  const opt = (r, id, name) => (r || []).map((x) => ({ value: x[id], label: `${x[name]} (${x[id]})` }));

  return (
    <div className="space-y-6" data-testid="followups-page">
      <PageHead eyebrow="CRM" title="Follow-ups"
                subtitle="Lead and application level follow-ups with outcome capture and automatic next-follow-up scheduling. Due and overdue items raise notifications." />
      <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-4">
        <Stat label="Open" value={every.filter((f) => f.status === "Open").length} testid="fu-open" />
        <Stat label="Due today" value={every.filter((f) => f.status === "Open" && f.due_date === new Date().toISOString().slice(0, 10)).length} testid="fu-today" />
        <Stat label="Overdue" value={every.filter((f) => f.overdue).length} testid="fu-overdue" />
        <Stat label="Completed" value={every.filter((f) => f.status === "Completed").length} testid="fu-completed" />
      </div>

      <Panel title="Schedule a follow-up" testid="create-followup-panel">
        <QuickForm testid="followup-form" values={form} submitLabel="Schedule follow-up"
                   onChange={(k, v) => setForm((f) => ({ ...f, [k]: v }))}
                   fields={[
                     { key: "lead_id", label: "Lead", options: opt(leads.data, "lead_id", "customer_name") },
                     { key: "followup_type", label: "Type", options: ["Call", "Meeting", "Email", "WhatsApp", "Document Collection", "Bank Visit", "Other"] },
                     { key: "subject", label: "Subject" },
                     { key: "due_date", label: "Due Date", type: "date" },
                     { key: "due_time", label: "Due Time", type: "time" },
                     { key: "priority", label: "Priority", options: ["Low", "Medium", "High", "Urgent"] },
                     { key: "employee_id", label: "Employee", options: opt(options.data?.employees, "employee_id", "employee_name") },
                     { key: "partner_id", label: "Partner", options: opt(options.data?.partners, "partner_id", "partner_name") },
                     { key: "remarks", label: "Remarks" },
                   ]}
                   onSubmit={async () => {
                     const r = await post("/crm/followups", form, "Follow-up scheduled");
                     if (r) { setForm({}); list.reload(); all.reload(); }
                   }} />
      </Panel>

      <Tabs tabs={Object.keys(TABS)} active={tab} onChange={setTab} testid="followup-tabs" />

      <Panel title={`${tab} follow-ups (${rows.length})`} testid="followups-panel">
        <DataTable testid="followups-table" rows={rows} empty="Nothing here — you're all caught up."
                   columns={[{ key: "followup_id", label: "ID" },
                     { key: "customer_name", label: "Customer" },
                     { key: "lead_id", label: "Lead" },
                     { key: "followup_type", label: "Type" },
                     { key: "subject", label: "Subject" },
                     { key: "due_date", label: "Due", render: (r) => `${fmtDate(r.due_date)}${r.overdue ? " ⚠" : ""}` },
                     { key: "priority", label: "Priority", render: (r) => <StatusBadge value={r.priority} /> },
                     { key: "employee_id", label: "Employee" },
                     { key: "partner_id", label: "Partner" },
                     { key: "status", label: "Status", render: (r) => <StatusBadge value={r.status} /> },
                     { key: "outcome", label: "Outcome" },
                     { key: "actions", label: "Actions", render: (r) => (
                       <span className="flex gap-1">
                         {r.status === "Open" && (
                           <Button size="sm" variant="outline" className="bg-white text-[10px]"
                                   data-testid={`close-followup-${r.followup_id}`}
                                   onClick={() => { setRow(r); setClose({ status: "Completed", outcome: "", next_followup: "" }); }}>
                             Complete</Button>)}
                         <Button size="sm" variant="outline" className="bg-white text-[10px]"
                                 data-testid={`open-followup-lead-${r.followup_id}`}
                                 onClick={() => nav(`/crm/leads/${r.lead_id}`)}>Lead</Button>
                       </span>) }]} />
      </Panel>

      <Dialog open={!!row} onOpenChange={() => setRow(null)}>
        <DialogContent className="max-w-md bg-white" data-testid="followup-dialog">
          <DialogHeader><DialogTitle>Complete follow-up {row?.followup_id}</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div className="space-y-1">
              <Label className="text-xs">Status</Label>
              <select className="h-9 w-full rounded-md border border-input bg-white px-2 text-sm"
                      data-testid="followup-status-select" value={close.status}
                      onChange={(e) => setClose((f) => ({ ...f, status: e.target.value }))}>
                {["Completed", "Cancelled"].map((s) => <option key={s} value={s}>{s}</option>)}
              </select>
            </div>
            <div className="space-y-1">
              <Label className="text-xs">Outcome</Label>
              <Input data-testid="followup-outcome-input" value={close.outcome}
                     onChange={(e) => setClose((f) => ({ ...f, outcome: e.target.value }))} />
            </div>
            <div className="space-y-1">
              <Label className="text-xs">Next follow-up date (optional)</Label>
              <Input type="date" data-testid="followup-next-input" value={close.next_followup}
                     onChange={(e) => setClose((f) => ({ ...f, next_followup: e.target.value }))} />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" className="bg-white" onClick={() => setRow(null)}>Cancel</Button>
            <Button data-testid="confirm-followup-btn"
                    onClick={async () => {
                      const r = await put(`/crm/followups/${row.followup_id}`, close, "Follow-up updated");
                      if (r) { setRow(null); list.reload(); all.reload(); }
                    }}>Save</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
